import { useIntl } from 'react-intl';
import { CircularProgress, Stack } from '@mui/material';
import PropTypes from 'prop-types';
import { useGetAiModelsByProviderIdQuery, useGetAiProvidersQuery } from 'services/aiProviderApi';
import { useGetScriptsQuery } from 'services/scriptApi';
import { BaseNode } from 'pages/assistants/builder/components/nodes/baseNode';
import { CompactNodeField } from 'pages/assistants/builder/components/nodes/compactNodeField';

export function GroovyNode({ id, data, handleConf, onEditNode, onDeleteNode }) {
  const { formatMessage: msg } = useIntl();
  const { providerCode, modelCode, scriptCode } = data;

  const { data: providers = [], isLoading: providersLoading } = useGetAiProvidersQuery();
  const { data: scripts = [], isLoading: scriptLoading } = useGetScriptsQuery();

  const filteredProviders = providers.filter((p) => p.displayableKey !== '***********');
  const selectedProvider =
    filteredProviders.find((p) => p.code === providerCode) ?? filteredProviders[0];
  const selectedProviderId = selectedProvider?.id;

  const { data: models = [], isLoading: modelsLoading } = useGetAiModelsByProviderIdQuery(
    selectedProviderId,
    { skip: !selectedProviderId }
  );

  const sortedModels = [...models].sort((a, b) => {
    if (a.favorite && !b.favorite) return -1;
    if (!a.favorite && b.favorite) return 1;
    return a.label.localeCompare(b.label);
  });

  return (
    <BaseNode
      id={id}
      icon="terminal_icon"
      nodeData={{ ...data, label: msg({ id: 'field.groovy.processor.label' }) }}
      color="error"
      handleConf={handleConf}
      onEditNode={onEditNode}
      onDeleteNode={onDeleteNode}
    >
      <Stack spacing={1} sx={{ p: 1, width: '280px' }}>
        {scriptLoading ? (
          <CircularProgress size={24} />
        ) : (
          <CompactNodeField
            label={msg({ id: 'field.script.name.label' })}
            select
            value={scriptCode || ''}
            options={[
              { value: '0', label: 'Sélectionner un script' },
              ...scripts.map((s) => ({ value: s.code, label: s.name }))
            ]}
          />
        )}

        {!providersLoading && (providerCode || modelCode) && (
          <>
            <CompactNodeField
              label={msg({ id: 'field.provider.label' })}
              select
              value={providerCode || ''}
              options={[
                { value: '0', label: 'Sélectionner un fournisseur' },
                ...filteredProviders.map((p) => ({ value: p.code, label: p.label }))
              ]}
            />

            {!modelsLoading && providerCode && (
              <CompactNodeField
                label={msg({ id: 'field.model.label' })}
                select
                value={modelCode || ''}
                options={[
                  { value: '0', label: msg({ id: 'field.model.default.label' }) },
                  ...sortedModels.map((m) => ({ value: m.code, label: m.label }))
                ]}
              />
            )}
          </>
        )}
      </Stack>
    </BaseNode>
  );
}

GroovyNode.propTypes = {
  id: PropTypes.string,
  data: PropTypes.shape({
    id: PropTypes.number,
    scriptCode: PropTypes.string,
    providerCode: PropTypes.string,
    modelCode: PropTypes.string,
    isAsynch: PropTypes.bool
  }),
  handleConf: PropTypes.array.isRequired,
  onEditNode: PropTypes.func.isRequired,
  onDeleteNode: PropTypes.func.isRequired
};
